/**
 * GrantSelectorSection — AC#5-#6 grant section under CostBearerStep.
 * STORY-1305 (Sprint 17 W1) + review patches 2026-05-13:
 * - P4: PERMANENT grantee partner name via usePartner(id) instead of raw UUID.
 * - P8: granteeData.name='' trim-and-fallback.
 * - P10: useGrant derived from grantId !== null (no useState drift).
 * - P11: grants loading guard (useActiveGrantsForGrantor isLoading honored).
 * - D1=C: pending-one-time card displayed when grantId === sentinel.
 *
 * Renders inline after CostBearerStep (NOT a separate wizard panel).
 */

import type { AuthorizationGrant } from '@/api/auth-grants';
import { useActiveGrantsForGrantor } from '@/hooks/use-auth-grants';
import { usePartner } from '@/hooks/use-partners';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { OneTimeGrantDialog } from '../components/OneTimeGrantDialog';
import { PENDING_ONE_TIME_SENTINEL, useWizardStore } from '../wizard-store';

function trimOrFallback(value: string | undefined | null, fallback: string): string {
  return value?.trim() ? value.trim() : fallback;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');
}

interface PermanentGrantCardProps {
  grant: AuthorizationGrant;
  selected: boolean;
  onSelect: () => void;
}

function PermanentGrantCard({ grant, selected, onSelect }: PermanentGrantCardProps) {
  const { t } = useTranslation();
  const { partner: granteePartner } = usePartner(grant.granteePartnerId ?? undefined);

  const displayName = trimOrFallback(
    grant.granteeData?.name,
    trimOrFallback(granteePartner?.name, grant.id.slice(0, 8))
  );

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full text-left p-3 rounded-md border bg-white dark:bg-gray-800 transition flex items-start gap-3',
        selected
          ? 'border-primary ring-2 ring-primary/30 bg-[var(--card-bg-selected)]'
          : 'border-gray-200 dark:border-[#2d7d8a] hover:border-primary/50'
      )}
      data-testid={`grant-card-${grant.id}`}
    >
      <span className="h-8 w-8 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
        {initials(displayName)}
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-gray-900 dark:text-white">{displayName}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
            {grant.type}
          </span>
          <span className="text-[10px] font-mono text-gray-500 dark:text-[#ccfbf1]">
            {grant.id.slice(0, 8)}
          </span>
        </span>
        <span className="block text-[11px] text-gray-500 dark:text-[#ccfbf1] mt-0.5">
          {t('rental.checkout.grant.permanentDropdown.validFrom', {
            date: new Date(grant.validFrom).toLocaleDateString('hu-HU'),
          })}
        </span>
      </span>
      {selected && (
        <svg
          className="h-5 w-5 shrink-0 text-primary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </button>
  );
}

export function GrantSelectorSection() {
  const { t } = useTranslation();
  const { partner, grantId, pendingOneTimeGrant, setGrantId } = useWizardStore();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { grants, isLoading } = useActiveGrantsForGrantor(partner?.id ?? null);

  // P10: derive directly from grantId — no useState drift when external code
  // (wizard-store.reset(), setPartner cascade) flips grantId to null.
  const useGrant = grantId !== null;
  const isPendingOneTime = grantId === PENDING_ONE_TIME_SENTINEL;

  const handleUseGrantToggle = (checked: boolean) => {
    if (checked) {
      // Opening the section without selecting anything yet — leave grantId as is.
      // If grantId was null, it stays null and the user picks below. We do NOT
      // optimistically set a sentinel just to drive the checkbox visual.
      return;
    }
    setGrantId(null);
  };

  if (!partner) return null;

  return (
    <div className="border-t border-gray-200 dark:border-[#2d7d8a] pt-5 mt-5">
      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={useGrant}
          onChange={e => handleUseGrantToggle(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          data-testid="grant-toggle-checkbox"
        />
        <span className="flex-1">
          <span className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
            {t('rental.checkout.grant.toggle.label')}
            <span className="text-[10px] text-gray-500 dark:text-[#ccfbf1] font-normal">
              {t('rental.checkout.grant.toggle.adrHint')}
            </span>
          </span>
          <span className="block text-[11px] text-gray-500 dark:text-[#ccfbf1] mt-0.5">
            {t('rental.checkout.grant.toggle.desc')}
          </span>
        </span>
      </label>

      {/* Section reveal: show list whenever grantId is set OR the user is browsing
          (no grantId yet but checkbox in indeterminate "open" state). Keep simple:
          show whenever there is something to render. */}
      {(useGrant || isLoading) && (
        <div className="mt-3 ml-7 pl-3 border-l-2 border-primary/20" data-testid="grant-reveal">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-[#ccfbf1] mb-2">
            {t('rental.checkout.grant.section.title')}
          </p>

          {isLoading && (
            <div className="py-3 text-center" data-testid="grant-loading">
              <div className="inline-block h-5 w-5 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
              <p className="mt-1 text-xs text-gray-500 dark:text-[#ccfbf1]">
                {t('rental.checkout.grant.loading')}
              </p>
            </div>
          )}

          {!isLoading && isPendingOneTime && pendingOneTimeGrant && (
            <div
              className="rounded-md border-2 border-primary bg-primary/5 p-3"
              data-testid="grant-pending-one-time-card"
            >
              <div className="flex items-start gap-3">
                <span className="h-8 w-8 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                  {initials(pendingOneTimeGrant.name)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900 dark:text-white">
                    {pendingOneTimeGrant.name}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-[#ccfbf1] mt-0.5">
                    {t('rental.checkout.grant.oneTime.pendingHint')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setGrantId(null)}
                  className="text-xs text-muted-foreground hover:text-red-600 underline"
                  data-testid="grant-pending-clear"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {!isLoading && !isPendingOneTime && grants.length > 0 && (
            <div className="space-y-2" data-testid="grant-list">
              <p className="text-[11px] text-gray-500 dark:text-[#ccfbf1]">
                {t('rental.checkout.grant.permanentDropdown.intro', { name: partner.name })}
              </p>
              {grants.map(g => (
                <PermanentGrantCard
                  key={g.id}
                  grant={g}
                  selected={grantId === g.id}
                  onSelect={() => setGrantId(g.id)}
                />
              ))}

              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="w-full px-3 py-2 rounded-md border border-dashed border-primary/50 text-primary hover:bg-primary/5 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="grant-one-time-trigger"
              >
                + {t('rental.checkout.grant.oneTime.cta')}
              </button>
            </div>
          )}

          {!isLoading && !isPendingOneTime && grants.length === 0 && (
            <div
              className="rounded-lg border-2 border-dashed border-gray-200 dark:border-[#2d7d8a] p-4 text-center"
              data-testid="grant-empty-state"
            >
              <p className="text-sm font-semibold text-gray-700 dark:text-[#ccfbf1]">
                {t('rental.checkout.grant.empty.title')}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-[#ccfbf1] mt-1">
                {t('rental.checkout.grant.empty.desc', { name: partner.name })}
              </p>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="mt-3 px-4 py-2 rounded-md bg-primary hover:bg-primary/90 text-white text-xs font-bold inline-flex items-center gap-1.5"
                data-testid="grant-one-time-trigger-empty"
              >
                + {t('rental.checkout.grant.oneTime.cta')}
              </button>
              <p className="mt-2 text-[10px] text-gray-500 dark:text-[#ccfbf1]">
                {t('rental.checkout.grant.empty.tip')}
              </p>
            </div>
          )}
        </div>
      )}

      <OneTimeGrantDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        grantorName={partner.name}
      />
    </div>
  );
}
